import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Droplet, LogOut, Loader2, Phone, Mail, MapPin, RefreshCw, KeyRound, Clock, Send, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { images } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pws_admin_token";

const statusColors = {
  new: "bg-pristine-aqua text-pristine-blue",
  contacted: "bg-amber-100 text-amber-700",
  closed: "bg-emerald-100 text-emerald-700",
};
const emailColors = {
  sent: "bg-emerald-100 text-emerald-700",
  pending: "bg-slate-100 text-slate-600",
  failed: "bg-red-100 text-red-700",
};

const errStr = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Something went wrong.");

function Shell({ children }) {
  return <div className="min-h-screen aqua-gradient flex items-center justify-center px-5 py-10">{children}</div>;
}

function Login({ onResult }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onResult(data.must_change_password ? "change" : "dashboard");
    } catch (err) {
      setError(errStr(err.response?.data?.detail) || "Login failed.");
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <form onSubmit={submit} className="w-full max-w-sm rounded-[2rem] bg-white border border-pristine-aqua shadow-[0_20px_60px_rgba(15,58,112,0.12)] p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-6">
          <img src={images.logo} alt="Pristine Water Systems" className="h-9 w-auto" />
          <div className="border-l border-pristine-aqua pl-3">
            <p className="font-heading text-base font-semibold text-pristine-blue leading-none">Admin</p>
            <p className="text-xs text-pristine-navy/50 mt-1">Enquiries Dashboard</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-pristine-navy font-medium">Email</Label>
            <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12 rounded-xl bg-pristine-surface border-pristine-aqua" />
          </div>
          <div>
            <Label className="text-pristine-navy font-medium">Password</Label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-12 rounded-xl bg-pristine-surface border-pristine-aqua" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full rounded-full bg-pristine-blue hover:bg-pristine-bluehover text-white h-12 font-semibold">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
          </Button>
        </div>
      </form>
    </Shell>
  );
}

function ChangePassword({ onResult }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 10) return setError("New password must be at least 10 characters.");
    if (next !== confirm) return setError("New passwords do not match.");
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const { data } = await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Password updated");
      onResult("dashboard");
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); onResult("login"); return; }
      setError(errStr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Shell>
      <form onSubmit={submit} className="w-full max-w-sm rounded-[2rem] bg-white border border-pristine-aqua shadow-[0_20px_60px_rgba(15,58,112,0.12)] p-8" data-testid="admin-change-password-form">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="h-6 w-6 text-pristine-aquadeep" />
          <p className="font-heading text-lg font-semibold text-pristine-blue">Set a new password</p>
        </div>
        <p className="text-sm text-pristine-navy/60 mb-6">For security, please change your temporary password before continuing.</p>
        <div className="space-y-4">
          <div>
            <Label className="text-pristine-navy font-medium">Current (temporary) password</Label>
            <Input data-testid="cp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-2 h-12 rounded-xl bg-pristine-surface border-pristine-aqua" />
          </div>
          <div>
            <Label className="text-pristine-navy font-medium">New password</Label>
            <Input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="At least 10 characters" className="mt-2 h-12 rounded-xl bg-pristine-surface border-pristine-aqua" />
          </div>
          <div>
            <Label className="text-pristine-navy font-medium">Confirm new password</Label>
            <Input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 h-12 rounded-xl bg-pristine-surface border-pristine-aqua" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="cp-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="cp-submit" className="w-full rounded-full bg-pristine-blue hover:bg-pristine-bluehover text-white h-12 font-semibold">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update Password"}
          </Button>
        </div>
      </form>
    </Shell>
  );
}

function Dashboard({ onResult }) {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

  const handle401or403 = (err) => {
    const s = err.response?.status;
    if (s === 401) { localStorage.removeItem(TOKEN_KEY); onResult("login"); return true; }
    if (s === 403) { onResult("change"); return true; }
    return false;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, authHeader());
      setEnquiries(data);
    } catch (err) { handle401or403(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}?status=${status}`, {}, authHeader());
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    } catch (err) { if (!handle401or403(err)) toast.error("Could not update status."); }
  };

  const retryEmail = async (id) => {
    setRetrying(id);
    try {
      const { data } = await axios.post(`${API}/enquiries/${id}/retry-email`, {}, authHeader());
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, email_status: data.email_status } : e)));
      toast.success("Notification sent.");
    } catch (err) {
      if (!handle401or403(err)) {
        const status = err.response?.status;
        if (status && status >= 500) {
          setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, email_status: "failed" } : e)));
        }
        toast.error(errStr(err.response?.data?.detail));
      }
    } finally { setRetrying(null); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); onResult("login"); };

  return (
    <div className="min-h-screen bg-pristine-surface">
      <header className="bg-white border-b border-pristine-aqua sticky top-0 z-10">
        <div className="mx-auto max-w-6xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={images.logo} alt="Pristine Water Systems" className="h-7 w-auto" />
            <span className="font-heading text-lg font-semibold text-pristine-blue border-l border-pristine-aqua pl-2">Enquiries</span>
            <Badge className="ml-2 bg-pristine-aqua text-pristine-blue hover:bg-pristine-aqua" data-testid="enquiry-count">{enquiries.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="admin-refresh" className="rounded-full border-pristine-aqua text-pristine-blue">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout" className="rounded-full border-pristine-aqua text-pristine-navy">
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        {loading && enquiries.length === 0 ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-pristine-aquadeep" /></div>
        ) : enquiries.length === 0 ? (
          <div className="text-center py-20 text-pristine-navy/50" data-testid="no-enquiries">No enquiries yet.</div>
        ) : (
          <div className="grid gap-4">
            {enquiries.map((e) => (
              <div key={e.id} data-testid={`enquiry-row-${e.id}`} className="rounded-2xl bg-white border border-pristine-aqua p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-heading text-lg font-semibold text-pristine-navy">{e.name}</h3>
                      <Badge className={`${statusColors[e.status] || statusColors.new} hover:opacity-90`}>{e.status}</Badge>
                      <Badge variant="outline" className="border-pristine-aquadeep text-pristine-aquadeep" data-testid={`source-${e.id}`}>
                        {e.source === "hero_callback" ? "Callback" : "Quote"}
                      </Badge>
                      <Badge className={`${emailColors[e.email_status] || emailColors.pending}`} data-testid={`email-status-${e.id}`}>
                        {e.email_status === "sent" ? "Email sent" : e.email_status === "failed" ? "Email failed" : "Email pending"}
                      </Badge>
                    </div>
                    <p className="text-xs text-pristine-navy/50 mt-1">{new Date(e.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-2">
                    {["new", "contacted", "closed"].map((s) => (
                      <button key={s} onClick={() => updateStatus(e.id, s)} data-testid={`status-${s}-${e.id}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${e.status === s ? "bg-pristine-blue text-white border-pristine-blue" : "border-pristine-aqua text-pristine-navy/60 hover:border-pristine-aquadeep"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <a href={`tel:${e.phone}`} className="inline-flex items-center gap-2 text-pristine-blue"><Phone className="h-4 w-4" /> {e.phone}</a>
                  {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 text-pristine-blue break-all"><Mail className="h-4 w-4 shrink-0" /> {e.email}</a>}
                  {e.suburb && <span className="inline-flex items-center gap-2 text-pristine-navy/70"><MapPin className="h-4 w-4" /> {e.suburb}</span>}
                  {e.callback_time && <span className="inline-flex items-center gap-2 text-pristine-navy/70"><Clock className="h-4 w-4" /> Callback: {e.callback_time}</span>}
                  {e.preferred_contact && <span className="text-pristine-navy/70">Prefers: {e.preferred_contact}</span>}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {e.water_source && <Badge variant="outline" className="border-pristine-aqua text-pristine-navy/70">Source: {e.water_source}</Badge>}
                  {e.service_required && <Badge variant="outline" className="border-pristine-aqua text-pristine-navy/70">Service: {e.service_required}</Badge>}
                  {(e.email_status === "failed" || e.email_status === "pending") && (
                    <Button size="sm" variant="outline" disabled={retrying === e.id} onClick={() => retryEmail(e.id)} data-testid={`retry-email-${e.id}`}
                      className="rounded-full border-pristine-blue text-pristine-blue h-8 ml-auto">
                      {retrying === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Retry notification</>}
                    </Button>
                  )}
                </div>

                {e.email_status === "failed" && (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-600" data-testid={`email-failed-note-${e.id}`}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Email notification failed to send.
                  </p>
                )}

                {e.description && <p className="mt-4 text-sm text-pristine-navy/70 bg-pristine-surface rounded-xl p-4">{e.description}</p>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [stage, setStage] = useState(localStorage.getItem(TOKEN_KEY) ? "dashboard" : "login");
  if (stage === "login") return <Login onResult={setStage} />;
  if (stage === "change") return <ChangePassword onResult={setStage} />;
  return <Dashboard onResult={setStage} />;
}
